import {type RefObject} from 'react';

import {type LayoutMode} from '../common';
import {type MediaBrowserPanelTrackingContextState} from '../MediaBrowserPanelTrackingContext';
import {getModeConfig} from '../modes/getModeConfig';
import {
  getTileCountBucket,
  type TrackingConfigContext,
  type TrackingSegmentation,
} from '../modes/trackingConfigHelper';

/**
 * Wall-clock stage timing captured during panel build.
 * Not emitted to Datadog directly — converted to {@link TrackingStage} at vital time.
 */
export type IntermediateStage = {
  startPerformanceMs: number;
  durationMs: number;
};

/** Build-stage timings written by {@link useMediaPanelValues} for tile-load vitals. */
export type PanelBuildMetrics = {
  stepMeta?: IntermediateStage;
  layoutGen?: IntermediateStage;
};

/**
 * Represents a single stage in the media loading pipeline.
 * Start times are relative to panel creation.
 */
export type TrackingStage = {
  startTime: number;
  duration: number;
};

function intermediateEndPerformanceMs(timing: IntermediateStage): number {
  return timing.startPerformanceMs + timing.durationMs;
}

function toTrackingStage(
  timing: IntermediateStage,
  panelStartPerformanceMs: number
): TrackingStage {
  return {
    startTime: Math.max(0, timing.startPerformanceMs - panelStartPerformanceMs),
    duration: timing.durationMs,
  };
}

/** Wall-clock panel origin for build-phase stage math; falls back for older events. */
export function getWallClockStart(
  panel: MediaBrowserPanelTrackingContextState
): number | undefined {
  const wallClock = panel.wallClockStartPerformanceMs;
  if (wallClock != null && wallClock > 0) {
    return wallClock;
  }
  const providerStart = panel.startTimePerformanceMs;
  if (providerStart != null && providerStart > 0) {
    return providerStart;
  }
  return undefined;
}

export function recordStepMetaBuildTiming(
  panelBuildMetricsRef: RefObject<PanelBuildMetrics>,
  timing: IntermediateStage
): void {
  if (panelBuildMetricsRef.current.stepMeta != null) {
    return;
  }
  panelBuildMetricsRef.current.stepMeta = timing;
}

export function recordLayoutGenBuildTiming(
  panelBuildMetricsRef: RefObject<PanelBuildMetrics>,
  timing: IntermediateStage,
  layoutReady: boolean
): void {
  if (!layoutReady || panelBuildMetricsRef.current.layoutGen != null) {
    return;
  }

  panelBuildMetricsRef.current.layoutGen = timing;
}

/**
 * All stages in the media tile loading pipeline.
 *
 * Each stage is tracked independently to identify performance bottlenecks.
 * See MEDIA_TRACKING_DATADOG.md for detailed documentation.
 */
export type TrackingStages = {
  /** Component setup before tile mount, excluding instrumented sub-stages (stepMeta, …). */
  processing: TrackingStage;
  /** `useStepMeta` memo duration — time to build StepMeta from run history rows. */
  stepMeta?: TrackingStage;
  /** `useLayoutGeneration` duration for this panel render. */
  layoutGen?: TrackingStage;
  /** Wall-clock from layout ready until the tile tracking provider mounts. */
  reactRender?: TrackingStage;
  /**
   * Time to receive available file metadata from API.
   * Omitted when the AvailableFiles stamp predates this tile mount.
   */
  files?: TrackingStage;
  /**
   * Time to resolve direct media URLs (CDN, signed URLs).
   * Omitted when URL stamps predates this tile mount.
   */
  urls?: TrackingStage;
  /**
   * Network download of media blob (can be slow for large files).
   * Omitted when blob stamps predates this tile mount.
   */
  blobFetch?: TrackingStage;
  /**
   * Memory operations to cache blob (object URL creation).
   * Omitted when cache stamps predates this tile mount.
   */
  cacheUpdate?: TrackingStage;
  /**
   * Time after the pipeline writes the cache until `TileMediaLoader` observes
   * `isComplete`. On the `IN_MEMORY_CACHE` path this is the 100ms poll wait
   * (typically 0–100ms). On `useFileData` it should be ~0 because Apollo
   * re-renders instead of polling.
   *
   * Omitted when there is no poll to measure: cache hits, missing
   * `statusObserved`, or stale `dataCached` from a prior mount.
   */
  pollWait?: TrackingStage;
  /** Time to load and render the actual media asset */
  load: TrackingStage;
};

export type TrackingState = {
  componentCreatedPerformanceMs: number;
  availableFilesReceivedPerformanceMs: number;
  directUrlReceivedPerformanceMs: number;
  blobFetchCompletePerformanceMs: number;
  dataCachedPerformanceMs: number;
  /**
   * When `TileMediaLoader` first observes `isComplete`. 0 means the stamp
   * was never written (older events / tests); `pollWait` is omitted and
   * `load` still spans dataCached → mediaLoaded.
   */
  statusObservedPerformanceMs?: number;
  /**
   * Set before emit. When true, `pollWait` is omitted (no status poll on
   * cache-served loads).
   */
  isCacheHit?: boolean;
  mediaLoadedPerformanceMs: number;
  panel: MediaBrowserPanelTrackingContextState;
};

/**
 * Pipeline stamps written before this tile mounted belong to a prior fetch
 * (typically first-write `IN_MEMORY_CACHE` on remount). Diffing them against
 * a fresh `componentCreated` invents negative / orphaned stage durations —
 * those stages are omitted from the result so Datadog keeps the original
 * fetch's vitals as the source of truth.
 *
 * Path-agnostic: Apollo first-mount cache hits stamp after mount, so stages
 * stay real (~0).
 */
export function pipelineStampBelongsToThisMount(
  stampPerformanceMs: number,
  componentCreatedPerformanceMs: number
): boolean {
  return stampPerformanceMs >= componentCreatedPerformanceMs;
}

function splitProcessingStages(
  panelStartPerformanceMs: number,
  componentCreatedPerformanceMs: number,
  panelBuildMetricsRef?: RefObject<PanelBuildMetrics>
): Pick<
  TrackingStages,
  'processing' | 'stepMeta' | 'layoutGen' | 'reactRender'
> {
  const totalProcessing =
    componentCreatedPerformanceMs - panelStartPerformanceMs;

  const stepMetaTiming = panelBuildMetricsRef?.current.stepMeta;
  const layoutGenTiming = panelBuildMetricsRef?.current.layoutGen;
  const layoutGenEndPerformanceMs = layoutGenTiming
    ? intermediateEndPerformanceMs(layoutGenTiming)
    : undefined;

  let reactRender: TrackingStage | undefined;
  if (
    layoutGenEndPerformanceMs != null &&
    layoutGenEndPerformanceMs < componentCreatedPerformanceMs
  ) {
    reactRender = {
      startTime: Math.max(
        0,
        layoutGenEndPerformanceMs - panelStartPerformanceMs
      ),
      duration: componentCreatedPerformanceMs - layoutGenEndPerformanceMs,
    };
  }

  const instrumentedDuration =
    (stepMetaTiming?.durationMs ?? 0) +
    (layoutGenTiming?.durationMs ?? 0) +
    (reactRender?.duration ?? 0);

  const result: Pick<
    TrackingStages,
    'processing' | 'stepMeta' | 'layoutGen' | 'reactRender'
  > = {
    processing: {
      startTime: 0,
      duration: Math.max(0, totalProcessing - instrumentedDuration),
    },
  };

  if (stepMetaTiming) {
    result.stepMeta = toTrackingStage(stepMetaTiming, panelStartPerformanceMs);
  }

  if (layoutGenTiming) {
    result.layoutGen = toTrackingStage(
      layoutGenTiming,
      panelStartPerformanceMs
    );
  }

  if (reactRender) {
    result.reactRender = reactRender;
  }

  return result;
}

/**
 * Calculates duration and timing for each stage of media tile loading.
 *
 * This function breaks down the media loading pipeline into discrete stages,
 * calculating both the duration of each stage and its start time relative to
 * the panel creation. These stages are then sent to Datadog as separate vitals
 * for independent analysis.
 *
 * Pipeline flow:
 * 1. processing:   Panel created → Component mounted (residual after sub-stages)
 * 2. stepMeta:     CPU to build step metadata (optional)
 * 3. layoutGen:    CPU to build layout (optional)
 * 4. reactRender:  Layout ready → tile mount (optional, derived)
 * 5. files:        Component mounted → Files metadata received
 * 6. urls:         Files received → Direct URLs resolved
 * 7. blobFetch:    URLs resolved → Blob downloaded (network)
 * 8. cacheUpdate:  Blob ready → Data cached (memory)
 * 9. pollWait:     Data cached → TileMediaLoader observes isComplete (100ms poll)
 * 10. load:        Status observed → Media loaded and rendered
 *     (If statusObserved is missing, load still spans dataCached → mediaLoaded.)
 *
 * @param state - Current tracking state with all timestamp measurements
 * @returns TrackingStages object with timing for each stage, or null if incomplete
 *
 * @example
 * const stages = calculateTrackingStages(trackingState, panelBuildMetricsRef);
 * if (stages) {
 *   console.log(`Blob fetch took ${stages.blobFetch.duration}ms`);
 *   console.log(`Cache update took ${stages.cacheUpdate.duration}ms`);
 * }
 */
export function calculateTrackingStages(
  state: TrackingState,
  panelBuildMetricsRef?: RefObject<PanelBuildMetrics>
): TrackingStages | null {
  const wallClockStart = getWallClockStart(state.panel);
  if (!state.mediaLoadedPerformanceMs || wallClockStart == null) {
    return null;
  }

  const panelStartTimePerformance = wallClockStart;
  const componentCreated = state.componentCreatedPerformanceMs;
  const availableFiles = state.availableFilesReceivedPerformanceMs;
  const directUrl = state.directUrlReceivedPerformanceMs;
  const blobFetchComplete = state.blobFetchCompletePerformanceMs;
  const dataCached = state.dataCachedPerformanceMs;

  const filesOnThisMount = pipelineStampBelongsToThisMount(
    availableFiles,
    componentCreated
  );
  const urlsOnThisMount =
    filesOnThisMount &&
    pipelineStampBelongsToThisMount(directUrl, componentCreated);
  const blobFetchOnThisMount =
    urlsOnThisMount &&
    pipelineStampBelongsToThisMount(blobFetchComplete, componentCreated);
  const cacheUpdateOnThisMount =
    blobFetchOnThisMount &&
    pipelineStampBelongsToThisMount(dataCached, componentCreated);
  const dataCachedOnThisMount = pipelineStampBelongsToThisMount(
    dataCached,
    componentCreated
  );

  const relativeStart = (stampPerformanceMs: number) =>
    Math.max(0, stampPerformanceMs - panelStartTimePerformance);

  const statusObservedPerformanceMs = state.statusObservedPerformanceMs ?? 0;
  const hasStatusObservedStamp = statusObservedPerformanceMs > 0;
  // Stale dataCached must not anchor load — that invents a huge "load" from
  // prior-fetch cache time until this mount's render.
  const loadStartPerformanceMs = hasStatusObservedStamp
    ? Math.max(
        statusObservedPerformanceMs,
        dataCachedOnThisMount ? dataCached : componentCreated
      )
    : dataCachedOnThisMount
      ? dataCached
      : componentCreated;

  const processingStages = splitProcessingStages(
    panelStartTimePerformance,
    componentCreated,
    panelBuildMetricsRef
  );

  const stages: TrackingStages = {
    ...processingStages,
    load: {
      startTime: relativeStart(loadStartPerformanceMs),
      duration: state.mediaLoadedPerformanceMs - loadStartPerformanceMs,
    },
  };

  // Omit fetch stages whose stamps belong to a prior mount — the original
  // emit already recorded those durations; fabricating 0s here skews p75s.
  if (filesOnThisMount) {
    stages.files = {
      startTime: relativeStart(componentCreated),
      duration: availableFiles - componentCreated,
    };
  }
  if (urlsOnThisMount) {
    stages.urls = {
      startTime: relativeStart(availableFiles),
      duration: directUrl - availableFiles,
    };
  }
  if (blobFetchOnThisMount) {
    stages.blobFetch = {
      startTime: relativeStart(directUrl),
      duration: blobFetchComplete - directUrl,
    };
  }
  if (cacheUpdateOnThisMount) {
    stages.cacheUpdate = {
      startTime: relativeStart(blobFetchComplete),
      duration: dataCached - blobFetchComplete,
    };
  }
  // Omit pollWait when there was no poll (cache hit), no observe stamp, or
  // stale dataCached (would otherwise report cache age).
  if (!state.isCacheHit && hasStatusObservedStamp && dataCachedOnThisMount) {
    stages.pollWait = {
      startTime: relativeStart(dataCached),
      duration: Math.max(0, statusObservedPerformanceMs - dataCached),
    };
  }

  return stages;
}

/**
 * Calculates tile-load duration for legacy total vitals.
 *
 * Measures from tracking-provider mount until media finished loading — the
 * pre-Aug-31 definition shared with `mediaBrowserPanelTracking`.
 *
 * @param state - Current tracking state with all timestamp measurements
 * @returns Total duration in milliseconds, or null if loading not yet complete
 */
export function calculateTotalDuration(state: TrackingState): number | null {
  if (!state.mediaLoadedPerformanceMs || !state.panel?.startTimePerformanceMs) {
    return null;
  }
  return state.mediaLoadedPerformanceMs - state.panel.startTimePerformanceMs;
}

export type GetTrackingSegmentationParams = {
  activeTileCount: number;
  layoutTileCount: number;
  panelMode: LayoutMode;
  mediaType?: TrackingConfigContext['mediaType'];
  columnCount?: number;
  compareRowCount?: number;
  fanOutVariable?: boolean;
};

/** Derives Datadog tile-load segmentation from layout counts and panel config. */
export function getTrackingSegmentation({
  activeTileCount,
  layoutTileCount,
  panelMode,
  mediaType = null,
  columnCount = 1,
  compareRowCount = 0,
  fanOutVariable = false,
}: GetTrackingSegmentationParams): TrackingSegmentation {
  const modeConfig = getModeConfig(panelMode);
  const trackingContext: TrackingConfigContext = {
    mediaType,
    columnCount,
    layoutTileCount,
    activeTileCount,
    compareRowCount,
    fanOutVariable,
  };
  const pageCap = modeConfig.getPageCap(trackingContext);
  return {
    activeTileCount,
    layoutTileCount,
    panelMode,
    pageCap,
    loadTier: modeConfig.getLoadTier({...trackingContext, pageCap}),
    tileCountBucket: getTileCountBucket(activeTileCount),
  };
}
